import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AbilityContext } from '../../../../components/ability/can';
import { PageHeader } from '../../../../components/PageHeader';
import { useDocumentTitle } from '../../../../components/useDocumentTitle';
import { ViewSwitcher } from '../../../../components/viewSwitcher/ViewSwitcher';
import { usePriceListQuery } from '../../../../domain/market-prices/queries';
import { priceResponseViewInterface } from '../priceResponseViewInterface';
import {
    calculatePriceResponseScenariosEvents,
    extractPriceSeries,
    ICalculateEventsInput,
    ICalculateEventsResult,
} from './calculatePriceResponseScenariosEvents';
import { PriceResponseScenariosPriceChart } from './PriceResponseScenariosPriceChart';
import { IScenarioSubmitValues, PriceResponseScenariosForm } from './PriceResponseScenariosForm';
import { PriceResponseScenariosEventsTable } from './PriceResponseScenariosEventsTable';
import { PriceResponseScenariosSummaryTable } from './PriceResponseScenariosSummaryTable';
import { PriceResponseMarket } from './PriceResponseMarket';

const EMPTY_EVENTS_RESULT: ICalculateEventsResult = {
    eventsWithCap: [],
    eventsWithoutCap: [],
    summaryWithCap: {},
    summaryWithoutCap: {},
};

export const PriceResponseScenarios = () => {
    const navigation = useNavigate();
    const ability = useContext(AbilityContext);
    const location = useLocation();

    useDocumentTitle('Price Response Scenarios');

    const [scenario, setScenario] = useState<IScenarioSubmitValues | null>(null);
    const [events, setEvents] = useState<ICalculateEventsResult>(EMPTY_EVENTS_RESULT);
    const [isCalculating, setIsCalculating] = useState(false);

    const prMarket = useMemo(() => {
        if (!scenario) return null;
        return new PriceResponseMarket(scenario.market, scenario.zone);
    }, [scenario]);

    const { data: priceData, isFetching } = usePriceListQuery(
        {
            market: scenario?.market,
            zone: scenario?.zone,
            start: scenario?.start ?? new Date(),
            end: scenario?.end ?? new Date(),
        },
        {
            enabled: !!scenario,
            keepPreviousData: true,
            staleTime: 5 * 60 * 1000,
        }
    );

    const rawPrices = useMemo(() => {
        if (!priceData?.length) return [];
        return priceData.flatMap(zoneData => zoneData.prices);
    }, [priceData]);

    const chartSeries = useMemo(() => {
        if (!scenario || !rawPrices.length) return [];
        return extractPriceSeries(rawPrices, scenario.priceType);
    }, [rawPrices, scenario]);

    const workerInput = useMemo<ICalculateEventsInput | null>(() => {
        if (!scenario || !rawPrices.length || !prMarket) return null;
        return {
            prices: rawPrices,
            priceType: scenario.priceType,
            triggerPrice: scenario.triggerPrice,
            eventDurationHours: scenario.eventDurationHours,
            stopIfPriceDrops: scenario.stopIfPriceDrops,
            cooldownHours: scenario.cooldownHours,
            mw: scenario.estimatedKw / 1000,
            maxDispatchesPerDay: scenario.maxDispatchesPerDay,
            market: prMarket,
        };
    }, [rawPrices, scenario, prMarket]);

    useEffect(() => {
        if (!workerInput) {
            setEvents(EMPTY_EVENTS_RESULT);
            setIsCalculating(false);
            return;
        }

        setIsCalculating(true);

        // Deferred by one tick so React commits + paints isCalculating=true before the
        // (now ~450ms) synchronous calculation runs. Without this, both state updates
        // would batch together in the same tick and the spinner would never be visible.
        const timeoutId = setTimeout(() => {
            const result = calculatePriceResponseScenariosEvents(workerInput);
            setEvents(result);
            setIsCalculating(false);
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [workerInput]);

    const onViewChange = (view: string) => {
        navigation(view);
    };

    return (
        <div style={{ width: '100%', maxWidth: 1600, margin: '0 auto' }}>
            <PageHeader
                pageTitle="Price Response Scenarios"
                extra={[
                    <ViewSwitcher
                        viewOptions={priceResponseViewInterface(ability)}
                        key="price-response-view-switcher"
                        view={location.pathname}
                        handleViewMode={onViewChange}
                    />,
                ]}
                actions={[]}
            />

            <PriceResponseScenariosForm isCalculating={isFetching} onCalculate={setScenario} />

            {scenario && !isFetching && (
                <>
                    <PriceResponseScenariosPriceChart
                        series={chartSeries}
                        triggerPrice={scenario.triggerPrice}
                        priceType={scenario.priceType}
                        market={prMarket!}
                        isLoading={isFetching || isCalculating}
                    />
                    <PriceResponseScenariosEventsTable
                        events={events}
                        isDefinedMaxDispatchesPerDay={!!scenario.maxDispatchesPerDay}
                        isLoading={isFetching || isCalculating}
                    />
                    <PriceResponseScenariosSummaryTable
                        events={events}
                        isDefinedMaxDispatchesPerDay={!!scenario.maxDispatchesPerDay}
                        isLoading={isFetching || isCalculating}
                    />
                </>
            )}
        </div>
    );
};
